import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { api, setAuthToken, setOnSessionEnded, setOnUnauthorized } from '../lib/api'

const Ctx = createContext(null)
const AUTH_KEY = 'kx.auth.v1'
const VARIANT_KEY = 'kx.variant.v1'

/**
 * Where a role belongs right after login. Kept here so the rule lives in ONE
 * place — the login modal used to send "anything that isn't a student" to
 * /admin, which dead-ends an instructor on the admin gate screen.
 *
 * Pakar dulu didaratkan di /me/profile karena itu satu-satunya permukaan yang
 * endpoint-nya boleh ia panggil. Sekarang ia punya ruang kerja sendiri di
 * /admin — dasbor, kelasnya, muridnya — jadi ke situlah ia diarahkan. Halaman
 * profil tetap ada, tapi mendaratkan seseorang di formulir biodata setiap kali
 * masuk menyiratkan bahwa itulah pekerjaannya di sini.
 */
export function homeFor(role) {
  if (role === 'platform_admin' || role === 'provider_admin' || role === 'instructor') return '/admin'
  return null // students stay wherever they were
}

export function AppProvider({ children }) {
  const [session, setSession] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(AUTH_KEY))
      if (s?.token) setAuthToken(s.token) // restore Bearer on reload
      return s
    } catch { return null }
  })
  const [loginOpen, setLoginOpen] = useState(false)
  const [afterLogin, setAfterLogin] = useState(null)
  const [toasts, setToasts] = useState([])
  const [variant, setVariant] = useState(() => {
    try { return { home: 'a', card: 'a', player: 'a', ...JSON.parse(localStorage.getItem(VARIANT_KEY) || '{}') } } catch { return { home: 'a', card: 'a', player: 'a' } }
  })

  useEffect(() => {
    if (session) localStorage.setItem(AUTH_KEY, JSON.stringify(session))
    else localStorage.removeItem(AUTH_KEY)
    setAuthToken(session?.token) // keep the Bearer in sync with the session
  }, [session])
  useEffect(() => { localStorage.setItem(VARIANT_KEY, JSON.stringify(variant)) }, [variant])

  const toast = useCallback((message, kind = 'success') => {
    const id = Math.random().toString(36).slice(2)
    setToasts((t) => [...t, { id, message, kind }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800)
  }, [])

  /**
   * Menerima sesi yang sudah jadi dari SSO.
   *
   * Tidak ada lagi `login(email, password)` di sini — `POST /auth/login` sudah
   * dihapus dari API. Satu-satunya cara sesi lahir adalah penukaran kode
   * otorisasi The Muslim, jadi tidak ada satu pun jalur di aplikasi ini yang
   * pernah menyentuh password.
   *
   * `afterLogin` tetap dijalankan: orang yang menekan "Daftar kelas" lalu
   * diminta masuk harus kembali ke kelas itu, bukan ke beranda.
   */
  const adoptSession = useCallback((data) => {
    // Pasang Bearer SEKARANG, jangan menunggu useEffect di bawah. Effect induk
    // jalan SETELAH effect anak, jadi komponen yang baru muncul karena login
    // (mis. halaman admin & OnboardingWidget) sempat memanggil API satu tik
    // tanpa token, dan endpoint yang longgar akan menjawabnya sebagai user
    // seed — bukan sebagai orang yang baru masuk.
    setAuthToken(data.access_token)
    setSession({
      token: data.access_token,
      user: data.user,
      refresh_token: data.refresh_token,
      // Disimpan untuk SATU keperluan: jadi `id_token_hint` saat Keluar, supaya
      // The Muslim mengakhiri sesinya tanpa bertanya lagi. Ia tidak dipakai
      // untuk apa pun di aplikasi ini dan tidak pernah dikirim ke endpoint lain.
      id_token: data.id_token || '',
    })
    setLoginOpen(false)
    toast(`Selamat datang, ${(data.user?.name || '').split(' ')[0]}!`)
    const next = afterLogin
    setAfterLogin(null)
    if (next) next()
    return data.user
  }, [afterLogin, toast])

  /**
   * Mulai login lewat The Muslim.
   *
   * `code_verifier` TIDAK dibuat di sini. Ia dibuat dan disimpan server, dan
   * itu bukan detail: verifier yang pernah dipegang peramban tidak
   * membuktikan apa pun, karena ia melewati jalur yang sama persis dengan
   * kode otorisasi yang ingin dilindunginya.
   *
   * Halaman asal dititipkan ke server lewat `redirect`, lalu dikembalikan di
   * balasan callback. Menyimpannya di sessionStorage juga bisa — sampai orang
   * menyelesaikan login di tab lain, dan mendarat di beranda tanpa tahu kenapa.
   */
  const startSSO = useCallback(async (redirectTo) => {
    const to = redirectTo || (window.location.pathname + window.location.search)
    const data = await api.get('/auth/login-url', { redirect: to })
    window.location.assign(data.authorize_url)
  }, [])

  // Sesi terakhir yang diketahui, dibaca lewat ref supaya `logout` dan handler
  // 401 di bawah tidak perlu dipasang ulang tiap kali sesi berubah.
  const sessionRef = useRef(session)
  useEffect(() => { sessionRef.current = session }, [session])

  /**
   * Keluar — dari KnowledgeX DAN dari The Muslim.
   *
   * Membersihkan sesi di sini saja tidak cukup, dan itu bukan soal kerapian.
   * KnowledgeX tidak punya login sendiri; sesi Django di The Muslim tetap
   * hidup, dan karena aplikasi ini `skip_authorization`, menekan Masuk
   * berikutnya menyelesaikan seluruh alur OAuth tanpa satu layar pun. Yang
   * terlihat: tombol Keluar yang tidak mengeluarkan siapa-siapa — dan di
   * komputer bersama, orang berikutnya cukup menekan Masuk untuk menjadi
   * orang sebelumnya.
   *
   * URUTANNYA: bersihkan lokal DULU, baru pindah. Kalau dibalik dan
   * perpindahannya gagal (jaringan mati, pemblokir, The Muslim sedang down),
   * orangnya tetap masuk di sini padahal sudah menekan Keluar.
   *
   * Permintaan ke server dibungkus try/catch dengan alasan yang sama: keluar
   * tidak boleh pernah gagal. Server yang tak terjangkau berarti sesi The
   * Muslim bertahan — separuh keluar — dan itu jauh lebih baik daripada
   * tombol Keluar yang tidak melakukan apa-apa.
   */
  const logout = useCallback(async () => {
    const s = sessionRef.current
    let url = ''
    try {
      const res = await api.post('/auth/logout', {
        refresh_token: s?.refresh_token || '',
        id_token: s?.id_token || '',
      })
      url = res?.logout_url || ''
    } catch { /* keluar tetap jalan */ }

    setSession(null)
    if (url) {
      window.location.assign(url)
      return
    }
    toast('Anda telah keluar', 'info')
  }, [toast])

  // Sesi ditolak server (token kedaluwarsa / dicabut): bersihkan dan minta masuk
  // lagi. Sebelum ada gerbang RequireAdmin, /admin/* melayani request tanpa token
  // sama sekali, jadi token kedaluwarsa tetap "berhasil" dan 401 praktis tidak
  // pernah muncul. Sekarang muncul — dan tanpa penanganan ini yang terlihat cuma
  // kotak merah di tiap panel, tanpa petunjuk bahwa obatnya login ulang.
  //
  // Status sesi dibaca dari ref, BUKAN dari dalam updater setSession: efek
  // samping di dalam updater dijalankan dua kali oleh StrictMode, jadi toast dan
  // modal login akan muncul dobel. Ref juga membuat handler ini tidak perlu
  // dipasang ulang tiap kali sesi berubah. (Ref-nya dideklarasikan di atas
  // `logout`, yang memakainya juga.)
  useEffect(() => {
    setOnUnauthorized(() => {
      if (!sessionRef.current) return // 401 nyasar saat sudah keluar: abaikan
      sessionRef.current = null       // tutup pintu untuk request barengan lain
      setSession(null)
      setLoginOpen(true)
      toast('Sesi Anda berakhir. Silakan masuk lagi.', 'info')
    })
    return () => setOnUnauthorized(null)
  }, [toast])

  // Orangnya keluar dari The Muslim di peramban ini, dan The Muslim memberi
  // tahu server kita (back-channel logout). Sesinya dibersihkan supaya layar
  // berhenti menampilkan nama dan avatar orang yang sudah keluar — di komputer
  // bersama, itulah seluruh gunanya.
  //
  // SENGAJA tidak membuka modal masuk, beda dengan 401 biasa di atas: ia baru
  // saja memilih keluar, bukan kehilangan sesi tanpa sengaja. Ia jadi
  // pengunjung biasa; tindakan yang butuh masuk tetap meminta masuk lewat
  // requireAuth seperti untuk tamu lain.
  useEffect(() => {
    setOnSessionEnded(() => {
      if (!sessionRef.current) return // sudah dibersihkan permintaan lain
      sessionRef.current = null
      setSession(null)
      toast('Anda sudah keluar dari The Muslim di peramban ini.', 'info')
    })
    return () => setOnSessionEnded(null)
  }, [toast])

  const requireAuth = useCallback((cb) => {
    if (session) return cb?.()
    setAfterLogin(() => cb)
    setLoginOpen(true)
    return null
  }, [session])

  const setVariantKey = useCallback((k, v) => setVariant((s) => ({ ...s, [k]: v })), [])

  const value = useMemo(() => ({
    session, user: session?.user || null, isAuthed: !!session,
    // NOTE: `instructor` deliberately does NOT count as an admin. isAdmin gates
    // the whole /admin portal and its 20-item CRUD sidebar, none of which an
    // expert is authorised for.
    isAdmin: ['platform_admin', 'provider_admin'].includes(session?.user?.role),
    isPlatformAdmin: session?.user?.role === 'platform_admin',
    isInstructor: session?.user?.role === 'instructor',
    logout, requireAuth, loginOpen, setLoginOpen,
    startSSO, adoptSession,
    toast, toasts, variant, setVariant: setVariantKey,
  }), [session, logout, requireAuth, loginOpen, startSSO, adoptSession, toast, toasts, variant, setVariantKey])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useApp = () => {
  const v = useContext(Ctx)
  if (!v) throw new Error('useApp must be used inside AppProvider')
  return v
}
